/**
 * Which vehicles players notice the moment they break.
 *
 * This table does one job: ranking. A missing texture on a background sedan and
 * a missing texture on the police cruiser every unit on the server drives are
 * the same severity and nowhere near the same problem, and a report that lists
 * them in file order buries the one that matters.
 *
 * ## Where the list comes from
 *
 * The stock half is not curated at all — it is Rockstar's own answer, read out of
 * `vehicles.meta` by `scripts/gta/build-vanilla-data.mjs`: `VC_EMERGENCY`, or
 * `FLAG_LAW_ENFORCEMENT`, or `FLAG_EMERGENCY_SERVICE`. That is a better list than
 * anyone would write by hand, and it updates itself when the game does.
 *
 * The curated half exists because the game's own answer is narrower than a
 * roleplay server's. Tow trucks and prison buses are emergency fleet in practice
 * and carry no emergency flag in the files; a server's fire department runs on
 * vehicles Rockstar never shipped.
 *
 * ## And why there is a heuristic as well
 *
 * A custom police car is called whatever its author called it. `lspd_charger22`,
 * `bcso_tahoe`, `sahp_explorer` — none of those are in any table and all of them
 * are the vehicle the server will notice first. The name patterns below catch
 * that class without pretending to certainty: `isEmergencyVehicleName` reports
 * *why* it matched, so a finding can say "this looks like a police vehicle"
 * rather than asserting it.
 */

import {
  VANILLA_EMERGENCY_MODELS,
  VANILLA_EMERGENCY_MODKIT_IDS,
} from "./vanillaVehicles.v1";

/**
 * Spawn names the game does not flag but a server treats as emergency fleet.
 *
 * Every entry is a judgement call, so every entry gets a reason. Removing one
 * should be as easy as reading why it is here and disagreeing.
 */
export const CURATED_EMERGENCY_VEHICLES: readonly (readonly [string, string])[] = [
  ["towtruck", "Tow is dispatched like emergency service on most roleplay servers"],
  ["towtruck2", "Tow is dispatched like emergency service on most roleplay servers"],
  ["towtruck3", "Tow is dispatched like emergency service on most roleplay servers"],
  ["towtruck4", "Tow is dispatched like emergency service on most roleplay servers"],
  ["flatbed", "Used as recovery alongside the tow fleet"],
  ["pbus2", "Prison bus; department fleet in practice"],
  ["boxville", "Commonly re-skinned as an EMS or coroner van"],
  ["boxville6", "Commonly re-skinned as an EMS or coroner van"],
  ["airtug", "Airport service vehicle that shares the emergency mod kit pool"],
  ["riata", "Frequently used as a park ranger vehicle"],
  ["brickade", "Frequently used as a mobile command post"],
];

/**
 * The full stock-plus-curated set, lower-cased.
 *
 * Exported as a `Set` because every consumer is asking "is this one", and the
 * two lists are joined here so no caller has to remember there were two.
 */
export const EMERGENCY_VEHICLE_NAMES: ReadonlySet<string> = new Set<string>([
  ...VANILLA_EMERGENCY_MODELS,
  ...CURATED_EMERGENCY_VEHICLES.map(([name]) => name),
]);

/**
 * Stock mod kit ids attached to at least one emergency vehicle.
 *
 * A custom kit that claims one of these does not just collide with "some stock
 * kit" — it collides with the police fleet, and the symptom is every cruiser on
 * the server losing its mods at once.
 */
export const EMERGENCY_STOCK_MODKIT_IDS: ReadonlySet<number> = new Set<number>(
  VANILLA_EMERGENCY_MODKIT_IDS,
);

/**
 * Name fragments that mark a *custom* vehicle as emergency service.
 *
 * Ordered longest-first within each group so the reported reason is the most
 * specific one that matched. Deliberately excludes two-letter abbreviations and
 * anything that reads as an ordinary English word: `ems` would match `lemons`,
 * `fd` would match half the alphabet, and a wrong emergency badge on a civilian
 * car costs more credibility than a missed one on a police car.
 */
const NAME_PATTERNS: readonly (readonly [RegExp, string])[] = [
  [/(^|[_-])(lspd|lssd|bcso|sahp|sast|nlsp|lspdfr)([_-]|\d|$)/i, "law enforcement department prefix"],
  [/(^|[_-])(police|sheriff|trooper|statepol|highway)/i, "law enforcement name"],
  [/^pol[a-z]/i, "Rockstar's `pol` prefix for police variants"],
  [/(^|[_-])(swat|noose|fib|fbi)([_-]|\d|$)/i, "tactical unit name"],
  [/(^|[_-])(unmarked|slicktop|ghost|interceptor)/i, "unmarked patrol naming"],
  [/(^|[_-])(ambulance|ambo|medic|paramedic|rescue|lifeguard)/i, "medical service name"],
  [/(^|[_-])(firetruk|firetruck|fireengine|ladder|engine\d|brush|pumper)([_-]|\d|$)/i, "fire service name"],
  [/(^|[_-])(coroner|hearse)([_-]|\d|$)/i, "coroner fleet name"],
  [/(^|[_-])(tow|wrecker|flatbed)([_-]|\d|$)/i, "recovery fleet name"],
];

export type EmergencyMatch = {
  /** Whether the name should be treated as emergency fleet at all. */
  emergency: boolean;
  /** How we know: an exact table hit is certain, a pattern hit is not. */
  confidence: "certain" | "likely";
  /** Phrasing for a finding: "…because it is a stock police vehicle". */
  reason: string;
};

const NOT_EMERGENCY: EmergencyMatch = {
  emergency: false,
  confidence: "certain",
  reason: "not a known or likely emergency vehicle",
};

/**
 * Whether a spawn name is emergency fleet, and how confident that is.
 *
 * Exact table hits come first: a stock name is a fact, and a pattern that
 * disagreed with one would be a bug rather than a nuance.
 */
export function isEmergencyVehicleName(name: string): EmergencyMatch {
  const normalized = name.trim().toLowerCase();
  if (normalized === "") return NOT_EMERGENCY;

  if (EMERGENCY_VEHICLE_NAMES.has(normalized)) {
    const curated = CURATED_EMERGENCY_VEHICLES.find(([entry]) => entry === normalized);
    return {
      emergency: true,
      confidence: "certain",
      reason: curated ? curated[1] : "a stock emergency-service vehicle",
    };
  }

  for (const [pattern, reason] of NAME_PATTERNS) {
    if (pattern.test(normalized)) {
      return { emergency: true, confidence: "likely", reason };
    }
  }

  return NOT_EMERGENCY;
}

/** Whether a mod kit id belongs to the stock emergency fleet. */
export function isEmergencyStockModkitId(id: number): boolean {
  return EMERGENCY_STOCK_MODKIT_IDS.has(id);
}
